import { assertNever } from '@deltasierra/shared';
import * as React from 'react';
import styled from 'styled-components';
import { DSPopperProps } from '../../../mui';
import { HoverPopperPopperMaxWidthType, HoverPopperPopper } from './HoverPopperPopper';

const StyledWrapper = styled.span<{ $margin: HoverPopperMarginType }>`
    display: inline-flex;
    vertical-align: middle;

    border-radius: 50%;

    ${({ $margin, theme }) => {
        const spacing = `${theme.spacing(1)}px`;
        switch ($margin) {
            case 'none':
                return '';
            case 'left':
                return `margin-left: ${spacing};`;
            case 'right':
                return `margin-right: ${spacing};`;
            case 'both':
                return `
                    margin-left: ${spacing};
                    margin-right: ${spacing};
                `;
            default:
                throw assertNever($margin);
        }
    }}
`;

export type HoverPopperMarginType = 'both' | 'left' | 'none' | 'right';

export type HoverPopperProps = {
    children: React.ReactNode;
    forceShow?: boolean;
    icon: JSX.Element;
    margin?: HoverPopperMarginType;
    maxWidth?: HoverPopperPopperMaxWidthType;
    placement?: DSPopperProps['placement'];
    usePaper?: boolean;
};

export function HoverPopper({
    children,
    forceShow = false,
    icon,
    margin = 'none',
    maxWidth = 'medium',
    placement = 'right',
    usePaper = true,
}: HoverPopperProps): JSX.Element {
    const anchorRef = React.useRef<HTMLElement | null>(null);
    const [show, setShow] = React.useState(false);

    const onMouseEnter = React.useCallback(() => {
        setShow(true);
    }, [setShow]);
    const onMouseLeave = React.useCallback(() => {
        setShow(false);
    }, [setShow]);

    const defaultOffsetMemo = React.useMemo(() => {
        function selectDefaultOffset() {
            if (placement.startsWith('top')) {
                return {
                    val1: 0,
                    val2: 15,
                };
            } else if (placement.startsWith('bottom')) {
                return {
                    val1: 0,
                    val2: 5,
                };
            } else {
                return {
                    val1: -5,
                    val2: 5,
                };
            }
        }
        const defaultOffset = selectDefaultOffset();
        return `${defaultOffset.val1}, ${defaultOffset.val2}`;
    }, [placement]);

    const modifiersMemo = React.useMemo(
        () => ({
            offset: { offset: defaultOffsetMemo },
        }),
        [defaultOffsetMemo],
    );

    return (
        <StyledWrapper $margin={margin} ref={anchorRef} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            {icon}
            <HoverPopperPopper
                anchorEle={anchorRef.current}
                maxWidth={maxWidth}
                modifiers={modifiersMemo}
                open={show || forceShow}
                placement={placement}
                usePaper={usePaper}
            >
                {children}
            </HoverPopperPopper>
        </StyledWrapper>
    );
}
HoverPopper.displayName = 'HoverPopper';
